<template>
  <div class="so-order-item-requests-verifier">
    <div v-if="requests.length === 0" class="empty-state">
      <p>{{ t('Nenhuma solicitação pendente') }}</p>
    </div>

    <div v-else class="requests-list">
      <div v-for="request in requests" :key="request.so_oir_id" class="request-row">
        <div class="request-header">
          <h4>{{ request.so_oir_description }}</h4>
          <div class="badges">
            <!-- Entry context: verified / pending -->
            <template v-if="context === 'entry'">
              <Badge
                v-if="Number(request.so_oir_verified) === 1"
                :data-testid="`verified-badge-${request.so_oir_id}`"
                :value="t('Verificado')"
                severity="success"
              />
              <Badge
                v-else
                :data-testid="`pending-badge-${request.so_oir_id}`"
                :value="t('Pendente')"
                severity="warning"
              />
            </template>
            <!-- Exit context: exit_confirmed / verified / pending -->
            <template v-else>
              <Badge
                v-if="Number(request.so_oir_exit_confirmed) === 1"
                :data-testid="`exit-confirmed-badge-${request.so_oir_id}`"
                :value="t('Realizado')"
                severity="success"
              />
              <Badge
                v-else-if="Number(request.so_oir_verified) === 1"
                :data-testid="`verified-small-badge-${request.so_oir_id}`"
                :value="t('Verificado')"
                severity="info"
              />
              <Badge
                v-else
                :data-testid="`pending-badge-${request.so_oir_id}`"
                :value="t('Pendente')"
                severity="warning"
              />
            </template>
          </div>
        </div>

        <!-- Entry context: just before-photo + verify button -->
        <template v-if="context === 'entry'">
          <a
            v-if="request.so_oir_photo"
            :href="request.so_oir_photo"
            target="_blank"
            rel="noopener noreferrer"
            class="photo-thumb"
            :data-testid="`request-photo-thumb-${request.so_oir_id}`"
            :title="t('Abrir foto em nova aba')"
          >
            <img :src="request.so_oir_photo" :alt="request.so_oir_description" />
          </a>
          <div class="request-actions">
            <AppButton
              v-if="Number(request.so_oir_verified) !== 1"
              :data-testid="`verify-btn-${request.so_oir_id}`"
              :label="t('Marcar como verificado')"
              icon="pi pi-check"
              severity="success"
              size="small"
              @click="handleVerify(request)"
            />
          </div>
        </template>

        <!-- Exit context: before / after photo grid + foto-depois capture -->
        <template v-else>
          <div class="photos-grid">
            <div class="photo-item">
              <label>{{ t('Foto Antes') }}</label>
              <a
                v-if="request.so_oir_photo"
                :href="request.so_oir_photo"
                target="_blank"
                rel="noopener noreferrer"
                class="photo-thumb"
                :data-testid="`request-photo-before-${request.so_oir_id}`"
                :title="t('Abrir foto em nova aba')"
              >
                <img :src="request.so_oir_photo" :alt="t('Foto antes')" />
              </a>
              <div v-else class="placeholder-image">
                {{ t('Sem foto') }}
              </div>
            </div>
            <div class="photo-item">
              <label>{{ t('Foto Depois') }}</label>
              <a
                v-if="request.so_oir_exit_photo && !editable"
                :href="request.so_oir_exit_photo"
                target="_blank"
                rel="noopener noreferrer"
                class="photo-thumb"
                :data-testid="`request-photo-after-${request.so_oir_id}`"
                :title="t('Abrir foto em nova aba')"
              >
                <img :src="request.so_oir_exit_photo" :alt="t('Foto depois')" />
              </a>
              <template v-else-if="editable">
                <a
                  v-if="request.so_oir_exit_photo"
                  :href="request.so_oir_exit_photo"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="photo-thumb"
                  :data-testid="`request-photo-after-${request.so_oir_id}`"
                  :title="t('Abrir foto em nova aba')"
                >
                  <img :src="request.so_oir_exit_photo" :alt="t('Foto depois')" />
                </a>
                <CameraCapture
                  :data-testid="`request-capture-after-${request.so_oir_id}`"
                  @photo-captured="(file: File) => handleAfterPhotoCaptured(request, file)"
                />
                <small v-if="capturingId === request.so_oir_id" class="capturing-hint">
                  {{ t('Enviando foto e confirmando...') }}
                </small>
                <AppButton
                  v-if="capturingId !== request.so_oir_id && Number(request.so_oir_exit_confirmed) !== 1"
                  :data-testid="`confirm-no-photo-btn-${request.so_oir_id}`"
                  :label="t('Confirmar sem foto')"
                  icon="pi pi-check"
                  severity="secondary"
                  size="small"
                  @click="handleConfirmWithoutPhoto(request)"
                />
              </template>
              <div v-else class="placeholder-image pending">
                {{ t('Aguardando confirmação') }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Badge from '../../../ui/Badge.vue';
import AppButton from '../../../ui/AppButton.vue';
import CameraCapture from '../../../components/CameraCapture.vue';
import { useSOOrderItemRequests } from '../../../composables/useSOOrderItemRequests';
import { eventBus } from '../../../eventBus';
import { log, error as logError } from '../../../utils/DebugLogger';

type Id = number | string;

interface OrderItemRequest {
  so_oir_id?: Id;
  so_oir_order_item_id?: Id;
  so_oir_description?: string;
  so_oir_photo?: string;
  so_oir_exit_photo?: string;
  so_oir_verified?: number | string;
  so_oir_exit_confirmed?: number | string;
  [key: string]: unknown;
}

interface Props {
  requests?: OrderItemRequest[];
  context?: 'entry' | 'exit';
  editable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  requests: () => [],
  context: 'entry',
  editable: true,
});

const emit = defineEmits<{
  'request-verified': [payload: { id: Id; order_item_id: Id }];
}>();

const { t } = useI18n();
const { verify, confirmExit, uploadPhoto } = useSOOrderItemRequests();

const capturingId = ref<Id | null>(null);

const handleAfterPhotoCaptured = async (request: OrderItemRequest, file: File): Promise<void> => {
  const requestId = request.so_oir_id;
  const orderItemId = request.so_oir_order_item_id;

  if (!requestId || !orderItemId) return;

  log('SOOrderItemRequestsVerifier', 'handleAfterPhotoCaptured', 'Uploading foto depois and confirming', {
    requestId,
    orderItemId,
    size: file.size,
  });

  capturingId.value = requestId;
  try {
    const photoUrl = await uploadPhoto(file) as string;
    const response = await confirmExit(Number(requestId), photoUrl) as { success?: boolean };

    if (response.success) {
      emit('request-verified', { id: requestId, order_item_id: orderItemId });
      eventBus.emit('so-order-item-request.verified', {
        id: requestId,
        order_item_id: orderItemId
      });
      eventBus.emit('globalMessage.success', t('Solicitação confirmada'));
    }
  } catch (err) {
    logError('SOOrderItemRequestsVerifier', 'handleAfterPhotoCaptured', 'Error', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao confirmar solicitação') + ': ' + message);
  } finally {
    capturingId.value = null;
  }
};

const handleConfirmWithoutPhoto = async (request: OrderItemRequest): Promise<void> => {
  const requestId = request.so_oir_id;
  const orderItemId = request.so_oir_order_item_id;
  if (!requestId || !orderItemId) return;

  log('SOOrderItemRequestsVerifier', 'handleConfirmWithoutPhoto', 'Confirming without photo', { requestId, orderItemId });
  capturingId.value = requestId;
  try {
    const response = await confirmExit(Number(requestId), '') as { success?: boolean };
    if (response.success) {
      emit('request-verified', { id: requestId, order_item_id: orderItemId });
      eventBus.emit('so-order-item-request.verified', { id: requestId, order_item_id: orderItemId });
      eventBus.emit('globalMessage.success', t('Solicitação confirmada'));
    }
  } catch (err) {
    logError('SOOrderItemRequestsVerifier', 'handleConfirmWithoutPhoto', 'Error', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao confirmar solicitação') + ': ' + message);
  } finally {
    capturingId.value = null;
  }
};

const handleVerify = async (request: OrderItemRequest): Promise<void> => {
  const requestId = request.so_oir_id;
  const orderItemId = request.so_oir_order_item_id;

  if (!requestId || !orderItemId) return;

  log('SOOrderItemRequestsVerifier', 'handleVerify', 'Verifying request', {
    requestId,
    orderItemId
  });

  try {
    const response = await verify(Number(requestId)) as { success?: boolean };

    if (response.success) {
      log('SOOrderItemRequestsVerifier', 'handleVerify', 'Request verified', {
        requestId,
        orderItemId
      });

      emit('request-verified', { id: requestId, order_item_id: orderItemId });
      eventBus.emit('so-order-item-request.verified', {
        id: requestId,
        order_item_id: orderItemId
      });
    }
  } catch (err) {
    logError('SOOrderItemRequestsVerifier', 'handleVerify', 'Error', err);
  }
};
</script>

<style scoped>
.so-order-item-requests-verifier {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.photo-thumb {
  display: inline-block;
  margin-top: 0.5rem;
  border: 1px solid var(--p-surface-border);
  border-radius: 4px;
  overflow: hidden;
  line-height: 0;
}

.photo-thumb img {
  width: 96px;
  height: 96px;
  object-fit: cover;
  display: block;
}

.request-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--p-surface-border);
  border-radius: 4px;
  background-color: var(--p-surface-50);
}

.photos-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.photo-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.photo-item > label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--p-text-muted-color);
}

.placeholder-image {
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--p-surface-100);
  border-radius: 4px;
  border: 1px dashed var(--p-surface-border);
  color: var(--p-text-muted-color);
  font-size: 0.8rem;
  text-align: center;
  padding: 8px;
}

.placeholder-image.pending {
  background: #fffbf0;
  border-color: #ffd89b;
  color: #b3860b;
}

.capturing-hint {
  display: block;
  color: var(--p-primary-color);
  font-style: italic;
  font-size: 0.75rem;
}

.request-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.request-header h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.badges {
  display: flex;
  gap: 0.5rem;
}

.photo-url {
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  color: var(--p-text-color);
}

.photo-url a {
  color: var(--p-primary-color);
  text-decoration: none;
  font-weight: 500;
}

.photo-url a:hover {
  text-decoration: underline;
}

.request-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
}
</style>
